import { Check, ChevronDown } from "lucide-react";
import { Select as S } from "radix-ui";
import {
  Children,
  forwardRef,
  Fragment,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

/** Radix não aceita valor vazio num item: "" vira este marcador por dentro. */
const EMPTY = "__kompil_empty__";
const toRadix = (v: string) => (v === "" ? EMPTY : v);
const fromRadix = (v: string) => (v === EMPTY ? "" : v);

interface Option {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

/** Lê os `<option>` filhos (também dentro de fragmentos e listas). */
function readOptions(children: ReactNode, out: Option[] = []): Option[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const el = child as ReactElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>;
    if (el.type === Fragment) return void readOptions(el.props.children, out);
    if (el.type === "option") {
      const label = el.props.children;
      out.push({
        value: String(el.props.value ?? (typeof label === "string" ? label : "")),
        label,
        disabled: el.props.disabled,
      });
    }
  });
  return out;
}

export interface SelectProps {
  value?: string | number;
  defaultValue?: string | number;
  /** Mesmo formato do `<select>` nativo: `e.target.value`. */
  onChange?: (e: { target: { value: string; name?: string } }) => void;
  children: ReactNode;
  name?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  title?: string;
  placeholder?: string;
  "aria-label"?: string;
  /** "field": campo de formulário; "ghost": discreto, para barras de ferramentas. */
  variant?: "field" | "ghost";
  /** Ícone antes do valor (ex.: cérebro no esforço do modelo). */
  icon?: ReactNode;
  align?: "start" | "center" | "end";
}

/**
 * Caixa de seleção do Kompil: a mesma API do `<select>` (com `<option>` dentro), mas a lista é
 * desenhada pelo app — tema, foco, teclado (setas, digitar para achar, Enter, Esc) e leitores de
 * tela —, no lugar da lista nativa do sistema, que não acompanha o visual.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    value,
    defaultValue,
    onChange,
    children,
    name,
    id,
    disabled,
    required,
    className,
    title,
    placeholder,
    variant = "field",
    icon,
    align = "start",
    "aria-label": ariaLabel,
  },
  ref,
) {
  const options = readOptions(children);
  const controlled = value !== undefined;
  const [inner, setInner] = useState(() => String(defaultValue ?? options[0]?.value ?? ""));
  const current = controlled ? String(value) : inner;
  const selected = options.find((o) => o.value === current);
  return (
    <S.Root
      value={toRadix(current)}
      onValueChange={(v) => {
        const real = fromRadix(v);
        if (!controlled) setInner(real);
        onChange?.({ target: { value: real, name } });
      }}
      disabled={disabled}
      required={required}
    >
      <S.Trigger
        ref={ref}
        id={id}
        title={title}
        aria-label={ariaLabel}
        className={cn(
          "group inline-flex min-w-0 items-center gap-1.5 text-left outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          variant === "field"
            ? "h-8 w-full justify-between rounded-md border border-border-strong bg-bg px-2.5 text-[13px] text-fg hover:border-border-strong focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 data-[state=open]:border-ring"
            : "h-7 rounded-md px-1.5 text-[12px] text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=open]:bg-bg-muted data-[state=open]:text-fg",
          className,
        )}
      >
        {icon ? <span className="flex shrink-0 items-center opacity-80">{icon}</span> : null}
        <span className={cn("min-w-0 flex-1 truncate", !selected && "text-fg-subtle")}>
          {selected ? selected.label : (placeholder ?? "Escolher…")}
        </span>
        <S.Icon asChild>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform duration-150 group-data-[state=open]:rotate-180" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className="z-[90] max-h-[min(320px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] animate-pop-in overflow-hidden rounded-lg border border-border bg-surface p-1 text-[13px] shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)]"
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={toRadix(o.value)}
                disabled={o.disabled}
                className="relative flex h-8 cursor-default select-none items-center gap-2 rounded-md pl-2 pr-7 text-fg outline-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg-emphasis data-[state=checked]:font-medium"
              >
                <S.ItemText>{o.label}</S.ItemText>
                <S.ItemIndicator className="absolute right-2 flex items-center">
                  <Check className="h-3.5 w-3.5" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
      {/* Formulários (FormData) recebem o valor real, também o vazio. */}
      {name ? <input type="hidden" name={name} value={current} /> : null}
    </S.Root>
  );
});
